import { createAsyncThunk } from '@reduxjs/toolkit';

import { config } from '@platform/constants';
import { httpService } from '@shared/utilities';
import { AscResponse } from '@shared/models';
import * as Response from '@shared/models/analysis/analysis.response.type';
import * as Request from '@shared/models/analysis/analysis.request.type';
import { formatErrorMessage } from '@ascendnow/ascend-components';
import { setSkillCategoryAnalysis, setSkillModules, setTrackPerModule, setTrackPerSkill } from '../slices/module.slice';

export const AnalysisProgressEndpoints = {
    getSkillCategoryAnalysis: (categoryId: string) => config.base_url + `/analysis/${categoryId}/per-module`,
    getSkillModules: (skillId: string) => config.base_url + `/analysis/per-skill/${skillId}`,
    getTrackProgressPerModule: (type: 'int' | 'ent') => config.base_url + `/analysis/tracks/${type}/per-module`,
    getTrackProgressPerSkill: (type: 'int' | 'ent', skillId: string) =>
        config.base_url + `/analysis/tracks/${type}/per-skill/${skillId}`,
};

export const fetchSkillCategoryAnalysis = createAsyncThunk<void, Request.IFetchSkillCategoryAnalysisQueryType>(
    AnalysisProgressEndpoints.getSkillCategoryAnalysis('id'),
    async (query, { dispatch }) => {
        try {
            const res = await httpService.get<AscResponse<Response.IFetchPerModuleAnalysisResponseType>>(
                AnalysisProgressEndpoints.getSkillCategoryAnalysis(query.params.categoryId)
            );

            dispatch(setSkillCategoryAnalysis(res.data.data));

            if (query.cb && query.cb.onSuccess) {
                query.cb.onSuccess(res.data.data);
            }
        } catch (err) {
            if (query.cb && query.cb.onError) {
                query.cb.onError(err);
            }
            console.log(
                formatErrorMessage(
                    err,
                    `Error fetching skills of analysis, query: ${AnalysisProgressEndpoints.getSkillCategoryAnalysis(
                        'id'
                    )}`
                ),
                { type: 'error' }
            );
        }
    }
);

export const fetchSkillModules = createAsyncThunk<void, Request.IFetchSkillModuleQueryType>(
    AnalysisProgressEndpoints.getSkillModules('id'),
    async (query, { dispatch }) => {
        try {
            const res = await httpService.get<AscResponse<Response.IFetchSkillModules>>(
                AnalysisProgressEndpoints.getSkillModules(query.params.skillId)
            );

            dispatch(setSkillModules(res.data.data));

            if (query.cb && query.cb.onSuccess) {
                query.cb.onSuccess(res.data.data);
            }
        } catch (err) {
            if (query.cb && query.cb.onError) {
                query.cb.onError(err);
            }
            console.log(
                formatErrorMessage(
                    err,
                    `Error fetching skill modules on analysis, query: ${AnalysisProgressEndpoints.getSkillModules(
                        'id'
                    )}`
                ),
                { type: 'error' }
            );
        }
    }
);

export const fetchTrackProgressPerModule = createAsyncThunk<void, Request.IFetchTrackPerModuleQueryType>(
    AnalysisProgressEndpoints.getTrackProgressPerModule('int'),
    async (query, { dispatch }) => {
        try {
            const params = new URLSearchParams({ take: String(query.params.take), skip: String(query.params.skip) });
            const res = await httpService.get<AscResponse<Response.IFetchTrackAnalysisPerModule>>(
                `${AnalysisProgressEndpoints.getTrackProgressPerModule(query.params.type)}?${params}`
            );

            dispatch(setTrackPerModule(res.data.data));

            if (query.cb && query.cb.onSuccess) {
                query.cb.onSuccess(res.data.data);
            }
        } catch (err) {
            if (query.cb && query.cb.onError) {
                query.cb.onError(err);
            }
            console.log(
                formatErrorMessage(
                    err,
                    `Error fetching track skills of analysis, query: ${AnalysisProgressEndpoints.getSkillCategoryAnalysis(
                        query.params.type
                    )}`
                ),
                { type: 'error' }
            );
        }
    }
);

export const fetchTrackProgressPerSkill = createAsyncThunk<void, Request.IFetchTrackPerSkillQueryType>(
    AnalysisProgressEndpoints.getTrackProgressPerSkill('int', 'id'),
    async (query, { dispatch }) => {
        try {
            const params = new URLSearchParams({ take: String(query.params.take), skip: String(query.params.skip) });
            const res = await httpService.get<AscResponse<Response.IFetchTrackAnalysisPerSkill>>(
                `${AnalysisProgressEndpoints.getTrackProgressPerSkill(
                    query.params.type,
                    query.params.skillId
                )}?${params}`
            );

            dispatch(setTrackPerSkill(res.data.data));

            if (query.cb && query.cb.onSuccess) {
                query.cb.onSuccess(res.data.data);
            }
        } catch (err) {
            if (query.cb && query.cb.onError) {
                query.cb.onError(err);
            }
            console.log(
                formatErrorMessage(
                    err,
                    `Error fetching track modules on analysis, query: ${AnalysisProgressEndpoints.getTrackProgressPerSkill(
                        query.params.type,
                        query.params.skillId
                    )}`
                ),
                { type: 'error' }
            );
        }
    }
);
